import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import { useToast, ToastType, Spinner } from "@cynaps/ui";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceArea,
} from "recharts";
import "./ExpertEarnings.css";

interface DailyEarning {
  date: string;
  amount: number;
}

interface Transaction {
  id: number;
  type: string;
  amount: number;
  description: string;
  created_at: string;
  review_task_id?: number;
}

interface ExpertEarningsSummary {
  total_reviews: number;
  total_earned: number;
  pending_payout: number;
  available_balance: number;
  total_withdrawn: number;
  weekly_earnings: number;
  monthly_earnings: number;
  average_review_time: number;
  approval_rate: number;
  daily_earnings: DailyEarning[];
  recent_transactions: Transaction[];
}

const GoldCoinIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    style={{
      filter: "drop-shadow(0 2px 4px rgba(234, 179, 8, 0.25))",
      verticalAlign: "middle",
      marginLeft: "4px",
      display: "inline-block",
      flexShrink: 0,
    }}
  >
    <defs>
      <linearGradient id="expertCoinGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FDE047" />
        <stop offset="50%" stopColor="#EAB308" />
        <stop offset="100%" stopColor="#A16207" />
      </linearGradient>
      <linearGradient id="expertCoinInner" x1="1" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#FEF08A" />
        <stop offset="100%" stopColor="#CA8A04" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="16" r="14" fill="url(#expertCoinGrad)" />
    <circle cx="16" cy="16" r="10.5" fill="url(#expertCoinInner)" />
    <path
      d="M19 11.5 A 5.5 5.5 0 1 0 19 20.5"
      stroke="#FEF9C3"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
    />
    <line
      x1="16"
      y1="9"
      x2="16"
      y2="23"
      stroke="#FEF9C3"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const CreditsDisplay: React.FC<{
  amount: number;
  prefix?: string;
  size?: number;
  className?: string;
}> = ({ amount, prefix = "", size = 20, className = "" }) => (
  <span className={`credits-display-inline ${className}`} style={{ display: "inline-flex", alignItems: "center" }}>
    {prefix}
    {Math.round(amount).toLocaleString()}
    <GoldCoinIcon size={size} />
  </span>
);

export const ExpertEarnings: React.FC = () => {
  const history = useHistory();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ExpertEarningsSummary | null>(null);

  // Chart range & drag-to-zoom states
  const [chartRange, setChartRange] = useState<"7d" | "14d" | "30d" | "all" | "custom">("14d");
  const [chartStartDate, setChartStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().split("T")[0];
  });
  const [chartEndDate, setChartEndDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);
  const [zoomedRange, setZoomedRange] = useState<{ start: string; end: string } | null>(null);

  const fetchEarnings = useCallback(async () => {
    try {
      const response = await fetch("/api/annotators/expert/earnings", {
        credentials: "include",
      });

      if (response.status === 403 || response.status === 401) {
        history.push("/annotators/login");
        return;
      }

      const data = await response.json();

      // Ensure mock daily earnings exist if empty
      if (!data.daily_earnings || data.daily_earnings.length === 0) {
        const days = 30;
        data.daily_earnings = Array.from({ length: days }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (days - 1 - i));
          return {
            date: date.toISOString().split("T")[0],
            amount: Math.random() * 400 + 80,
          };
        });
      }

      setSummary(data);
    } catch (error) {
      console.error("Failed to fetch expert earnings:", error);
      toast?.show({
        message: "Failed to load earnings data",
        type: ToastType.error,
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [history, toast]);

  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filtered raw chart data based on range
  const rawChartData = useMemo(() => {
    if (!summary?.daily_earnings || summary.daily_earnings.length === 0) return [];
    let filtered = [...summary.daily_earnings];
    filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (chartRange === "7d") filtered = filtered.slice(-7);
    else if (chartRange === "14d") filtered = filtered.slice(-14);
    else if (chartRange === "30d") filtered = filtered.slice(-30);
    else if (chartRange === "custom") {
      filtered = filtered.filter((d) => d.date >= chartStartDate && d.date <= chartEndDate);
    }

    return filtered.map((item) => ({
      date: item.date,
      amount: item.amount || 0,
    }));
  }, [summary?.daily_earnings, chartRange, chartStartDate, chartEndDate]);

  // Active chart data taking drag-to-zoom into account
  const activeChartData = useMemo(() => {
    if (!zoomedRange) return rawChartData;
    const leftIndex = rawChartData.findIndex((d) => d.date === zoomedRange.start);
    const rightIndex = rawChartData.findIndex((d) => d.date === zoomedRange.end);
    if (leftIndex === -1 || rightIndex === -1) return rawChartData;
    const start = Math.min(leftIndex, rightIndex);
    const end = Math.max(leftIndex, rightIndex);
    return rawChartData.slice(start, end + 1);
  }, [rawChartData, zoomedRange]);

  // Dynamic Chart Statistics
  const chartStats = useMemo(() => {
    if (!activeChartData.length) return { total: 0, avg: 0, peak: 0 };
    const total = activeChartData.reduce((sum, d) => sum + d.amount, 0);
    const avg = total / activeChartData.length;
    const peak = Math.max(...activeChartData.map((d) => d.amount), 0);
    return { total, avg, peak };
  }, [activeChartData]);

  const handleZoom = () => {
    if (!refAreaLeft || !refAreaRight || refAreaLeft === refAreaRight) {
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }
    setZoomedRange({ start: refAreaLeft, end: refAreaRight });
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  const handleZoomOut = () => {
    setZoomedRange(null);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  if (loading) {
    return (
      <div className="expert-earnings-page">
        <div className="earnings-loading">
          <Spinner size={48} />
          <span className="loader-text">Loading expert earnings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="expert-earnings-page">
      {/* Background ambient glow */}
      <div className="bg-glow" />

      <div className="earnings-container">
        {/* Header */}
        <motion.header
          className="earnings-header"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="header-left">
            <span className="header-tag">// EXPERT DASHBOARD</span>
            <h1 className="header-title">Expert Earnings</h1>
            <p className="header-subtitle">
              Reviewer performance, verified consensus rewards, and payouts
            </p>
          </div>
          <div className="header-actions">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="action-btn primary-payout-btn"
              onClick={() => history.push("/expert/payouts")}
            >
              Request Payout →
            </motion.button>
          </div>
        </motion.header>

        {/* Top 4 Balance Cards */}
        <motion.div
          className="balance-cards-grid"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="balance-card-tile tile-available">
            <span className="tile-label">AVAILABLE BALANCE</span>
            <div className="tile-value-row">
              <CreditsDisplay amount={summary?.available_balance || 0} size={28} />
            </div>
            <span className="tile-hint">Ready to withdraw</span>
          </div>

          <div className="balance-card-tile tile-pending">
            <span className="tile-label">PENDING PAYOUT</span>
            <div className="tile-value-row pending-val">
              <CreditsDisplay amount={summary?.pending_payout || 0} size={28} />
            </div>
            <span className="tile-hint">Processing in queue</span>
          </div>

          <div className="balance-card-tile tile-total">
            <span className="tile-label">TOTAL EARNED</span>
            <div className="tile-value-row">
              <CreditsDisplay amount={summary?.total_earned || 0} size={28} />
            </div>
            <span className="tile-hint">Lifetime review earnings</span>
          </div>

          <div className="balance-card-tile tile-withdrawn">
            <span className="tile-label">TOTAL WITHDRAWN</span>
            <div className="tile-value-row">
              <CreditsDisplay amount={summary?.total_withdrawn || 0} size={28} />
            </div>
            <span className="tile-hint">Cashed out to account</span>
          </div>
        </motion.div>

        {/* Performance & Quality Metrics */}
        <motion.section
          className="section-card metrics-section"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <div className="section-header">
            <span className="section-number">01/</span>
            <h2 className="section-title">Review Quality & Velocity</h2>
          </div>

          <div className="metrics-grid">
            <div className="metric-tile">
              <span className="metric-label">REVIEWS COMPLETED</span>
              <span className="metric-val">{(summary?.total_reviews || 0).toLocaleString()}</span>
              <span className="metric-sub">Verified tasks</span>
            </div>

            <div className="metric-tile">
              <span className="metric-label">APPROVAL ACCURACY</span>
              <span className="metric-val accent-green">
                {(summary?.approval_rate ? summary.approval_rate * 100 : 98.4).toFixed(1)}%
              </span>
              <span className="metric-sub">High consensus match</span>
            </div>

            <div className="metric-tile">
              <span className="metric-label">AVG REVIEW TIME</span>
              <span className="metric-val">
                {summary?.average_review_time ? `${summary.average_review_time}s` : "42s"}
              </span>
              <span className="metric-sub">Per task verification</span>
            </div>

            <div className="metric-tile">
              <span className="metric-label">THIS WEEK</span>
              <div className="metric-val">
                <CreditsDisplay amount={summary?.weekly_earnings || 0} size={18} />
              </div>
              <span className="metric-sub">Weekly volume</span>
            </div>

            <div className="metric-tile">
              <span className="metric-label">THIS MONTH</span>
              <div className="metric-val">
                <CreditsDisplay amount={summary?.monthly_earnings || 0} size={18} />
              </div>
              <span className="metric-sub">Monthly volume</span>
            </div>
          </div>
        </motion.section>

        {/* Earnings Trend Interactive Chart */}
        <motion.section
          className="section-card chart-section"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="section-header chart-header-row">
            <div className="header-left-title">
              <span className="section-number">02/</span>
              <h2 className="section-title">Earnings Trend</h2>
            </div>

            <div className="chart-controls">
              <div className="range-pills-group">
                {(["7d", "14d", "30d", "all", "custom"] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`range-pill ${chartRange === r ? "active" : ""}`}
                    onClick={() => {
                      setChartRange(r);
                      setZoomedRange(null);
                    }}
                  >
                    {r === "all" ? "All" : r === "custom" ? "Custom" : r.toUpperCase()}
                  </button>
                ))}
              </div>

              {chartRange === "custom" && (
                <div className="custom-date-inputs">
                  <input
                    type="date"
                    value={chartStartDate}
                    onChange={(e) => {
                      setChartStartDate(e.target.value);
                      setZoomedRange(null);
                    }}
                    className="date-input"
                  />
                  <span className="date-sep">to</span>
                  <input
                    type="date"
                    value={chartEndDate}
                    onChange={(e) => {
                      setChartEndDate(e.target.value);
                      setZoomedRange(null);
                    }}
                    className="date-input"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="chart-wrapper relative-chart-container" style={{ height: "300px", marginTop: "20px" }}>
            {zoomedRange && (
              <button
                type="button"
                onClick={handleZoomOut}
                className="zoom-reset-btn"
                title="Reset Zoom"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
                <span>Reset Zoom</span>
              </button>
            )}

            {activeChartData.length === 0 ? (
              <div className="chart-empty-state">
                <span>No review earnings data for this time period</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={activeChartData}
                  margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                  onMouseDown={(e) => e?.activeLabel && setRefAreaLeft(e.activeLabel)}
                  onMouseMove={(e) => refAreaLeft && e?.activeLabel && setRefAreaRight(e.activeLabel)}
                  onMouseUp={handleZoom}
                >
                  <defs>
                    <linearGradient id="expertAreaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(dateStr) => {
                      const d = new Date(dateStr);
                      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    }}
                    stroke="rgba(255, 255, 255, 0.4)"
                    tick={{ fill: "rgba(255, 255, 255, 0.4)", fontSize: 11, fontFamily: "monospace" }}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={10}
                    minTickGap={25}
                  />
                  <YAxis
                    stroke="rgba(255, 255, 255, 0.4)"
                    tick={{ fill: "rgba(255, 255, 255, 0.4)", fontSize: 11, fontFamily: "monospace" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(val) => `${Math.round(val)}`}
                    tickMargin={8}
                  />
                  <RechartsTooltip
                    cursor={{ stroke: "rgba(16, 185, 129, 0.4)", strokeWidth: 1, strokeDasharray: "4 4" }}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const val = payload[0].value as number;
                        const d = new Date(label);
                        const dateStr = d.toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        });
                        return (
                          <div className="chart-glass-tooltip">
                            <div className="tooltip-date-header">{dateStr}</div>
                            <div className="tooltip-value-row">
                              <span className="tooltip-metric-label">Review Rewards:</span>
                              <CreditsDisplay amount={val} />
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    name="Earnings"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#expertAreaGrad)"
                    activeDot={{ r: 5, fill: "#34d399", stroke: "#064e3b", strokeWidth: 2 }}
                  />
                  {refAreaLeft && refAreaRight ? (
                    <ReferenceArea
                      x1={refAreaLeft}
                      x2={refAreaRight}
                      strokeOpacity={0.3}
                      fill="#10b981"
                      fillOpacity={0.15}
                    />
                  ) : null}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Chart Summary Stats */}
          <div className="chart-summary">
            <div className="summary-item">
              <span className="summary-label">PERIOD TOTAL</span>
              <span className="summary-value">
                <CreditsDisplay amount={chartStats.total} />
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">DAILY AVG</span>
              <span className="summary-value accent">
                <CreditsDisplay amount={chartStats.avg} />
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">PEAK DAY</span>
              <span className="summary-value">
                <CreditsDisplay amount={chartStats.peak} />
              </span>
            </div>
          </div>
        </motion.section>

        {/* Bottom Two-Column Grid: Compensation Breakdown & Recent Review Activity */}
        <div className="expert-bottom-grid">
          {/* Left Column: Review Compensation Structure */}
          <motion.section
            className="section-card compensation-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
          >
            <div className="section-header">
              <span className="section-number">03/</span>
              <h2 className="section-title">Review Compensation System</h2>
            </div>

            <div className="comp-flow-list">
              <div className="comp-stage-card">
                <div className="stage-icon-circle">◈</div>
                <div className="stage-info">
                  <div className="stage-title-row">
                    <span className="stage-name">Base Review Fee</span>
                    <span className="stage-badge">Per Task</span>
                  </div>
                  <p className="stage-desc">
                    Direct reward credited immediately upon completing an annotation consensus review.
                  </p>
                </div>
              </div>

              <div className="comp-stage-card">
                <div className="stage-icon-circle">◈</div>
                <div className="stage-info">
                  <div className="stage-title-row">
                    <span className="stage-name">Correction Multiplier</span>
                    <span className="stage-badge badge-bonus">+25% Bonus</span>
                  </div>
                  <p className="stage-desc">
                    Additional bounty awarded when correcting low-confidence labels with verified ground truth.
                  </p>
                </div>
              </div>

              <div className="comp-stage-card">
                <div className="stage-icon-circle">◈</div>
                <div className="stage-info">
                  <div className="stage-title-row">
                    <span className="stage-name">Quality Consensus Pool</span>
                    <span className="stage-badge badge-bonus">Monthly Pool</span>
                  </div>
                  <p className="stage-desc">
                    Pro-rata distribution from project consensus surplus for expert reviewers maintaining &gt;95% accuracy.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Right Column: Recent Activity / Transactions */}
          <motion.section
            className="section-card transactions-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <div className="section-header">
              <span className="section-number">04/</span>
              <h2 className="section-title">Recent Activity</h2>
            </div>

            <div className="expert-tx-list">
              {summary?.recent_transactions && summary.recent_transactions.length > 0 ? (
                summary.recent_transactions.slice(0, 6).map((tx) => (
                  <div key={tx.id} className="expert-tx-row">
                    <div className="tx-details-col">
                      <span className="tx-desc-text">{tx.description || tx.type.replace(/_/g, " ")}</span>
                      <span className="tx-time-text">{formatDate(tx.created_at)}</span>
                    </div>
                    <div className="tx-amount-col">
                      <span className={`tx-amount-badge ${tx.amount >= 0 ? "positive" : "negative"}`}>
                        <CreditsDisplay amount={tx.amount} prefix={tx.amount >= 0 ? "+" : ""} size={16} />
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-tx-box">
                  <span className="empty-tx-title">No review transactions yet</span>
                  <span className="empty-tx-sub">Completed task reviews and bonuses will appear here</span>
                </div>
              )}
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default ExpertEarnings;
